import { addBreadcrumb } from '@sentry/react';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { t } from 'i18next';

import packageJson from '../../../../package.json';

import { LogLevel } from 'features/analytics/enums';
import { sendAnalytics } from 'features/analytics/store';
import type { RootState } from 'features/app/store/store';
import { LogoutType } from 'features/call/call-base/enums';
import { CallPageStatus } from 'features/call/call-ui-state/enums';
import {
  selectCallPageStatus,
  selectIsTeamingOfferCanceled,
} from 'features/call/call-ui-state/store';
import { leaveQueue } from 'features/leave-queue/store';
import { resolveSessionCallCenterCodeWithStorageFallback } from 'features/machine-preferences/helpers';
import { getMachinePreferencesSelector } from 'features/machine-preferences/store';
import { modeSelector, resetMultiMode } from 'features/multi-mode/store';
import { handleError, showNotification } from 'features/notification/store';
import { PageRoute } from 'features/router/routes';
import {
  InactiveOrInvalidCallCenterError,
  mapCallCenterInvalidResponse,
  autoUpdateSessionActivityTimeMs,
  finishTakingCallsAnalytics,
} from 'features/session/helpers';
import type {
  ReconnectSessionPayload,
  RemoveSessionPayload,
  SessionFinishResult,
  ReplaceSessionPayload,
  SessionFinishPayload,
} from 'features/session/interfaces';
import { SessionDaoService, SessionService } from 'features/session/services';
import {
  resetSessionId,
  rnsConnectionIdSelector,
  sessionRemovalCompleted,
  sessionRemovalFailed,
  sessionRemovalStatusSelector,
  sessionRemovalStarted,
  sessionIdSelector,
  setSessionId,
} from 'features/session/store';
import { actualSessionTeamingStatedSelector } from 'features/teaming/teaming-base/store';
import { sipRegistrationNotWanted } from 'features/sip/store';
import { RnsConnectionService } from 'features/rns/services';
import { callCenterCodeSelector } from 'features/user/store';

export const updateSessionActivity = createAsyncThunk(
  'session/updateActivity',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const callPageStatus = selectCallPageStatus(state);
    try {
      const rnsConnectionId = rnsConnectionIdSelector(state);
      const teamingState = actualSessionTeamingStatedSelector(state);

      await SessionDaoService.updateSessionActivity({
        rnsConnectionId,
        teamingState,
      });
    } catch (error) {
      const sessionId = sessionIdSelector(state);
      const session = await SessionDaoService.getSessionBySessionId(sessionId);
      if (!session) {
        dispatch(
          showNotification({ severity: 'info', title: t('sessionExpired') })
        );
        if (callPageStatus === CallPageStatus.IDLE) {
          dispatch(
            sendAnalytics({
              Level: LogLevel.INFO,
              Method: 'updateSessionActivity',
              Message: `User logged out due to invalid session`,
              sessionId: sessionId,
            })
          );
          dispatch(leaveQueue({ shouldFinishSession: false }));
        }
        return;
      }
      dispatch(handleError({ error, title: t('sessionUpdateFailed') }));
    }
  }
);

export const autoUpdateSessionActivity = createAsyncThunk(
  'session/autoUpdateActivity',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const intervalId = setInterval(async () => {
      try {
        const { pathname } = window.location;
        const isCallPage = [
          PageRoute.VRS_CALL,
          PageRoute.VRI_CALL,
          PageRoute.VRI_CONFERENCE,
          PageRoute.VRS_CONFERENCE,
        ].includes(pathname as PageRoute);
        const state = getState() as RootState;
        const isTeamingCanceled = selectIsTeamingOfferCanceled(state);

        if (!isCallPage || isTeamingCanceled) {
          clearInterval(intervalId);
          return;
        }

        dispatch(updateSessionActivity());
      } catch (error) {
        dispatch(handleError({ error }));
        return rejectWithValue(error);
      }
    }, autoUpdateSessionActivityTimeMs());
  }
);

export const createSession = createAsyncThunk(
  'session/create',
  async (rnsConnectionId: string, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const machinePreferences = getMachinePreferencesSelector(state);
      const userHomeCallCenterCode = callCenterCodeSelector(state);
      const mode = modeSelector(state);

      if (!mode) {
        throw new Error('Mode is required to create a session');
      }

      const resolvedCallCenter =
        resolveSessionCallCenterCodeWithStorageFallback(
          mode,
          machinePreferences.callCenterCode,
          userHomeCallCenterCode
        );

      if (!resolvedCallCenter.code) {
        throw new InactiveOrInvalidCallCenterError({
          source: resolvedCallCenter.source,
          callCenterCode: '',
          reason: 'missing',
        });
      }

      const resolvedMachinePreferences = {
        ...machinePreferences,
        callCenterCode: resolvedCallCenter.code,
      };

      const sessionService = SessionService.getInstance();
      const response = await SessionDaoService.createSession({
        rnsConnectionId,
        clientVersion: packageJson.version,
        machinePreferences: resolvedMachinePreferences,
      }).catch((error: unknown) => {
        throw (
          mapCallCenterInvalidResponse(error, resolvedCallCenter.source) ??
          error
        );
      });
      sessionService.setSession(response);
      dispatch(setSessionId(response.SessionLoginHistoryID));
      dispatch(autoUpdateSessionActivity());
      return response;
    } catch (error) {
      // useInitRnsSession shows the call center dialog message for this error.
      dispatch(
        handleError({
          error: error as Error,
          disableUserNotification:
            error instanceof InactiveOrInvalidCallCenterError,
        })
      );
      return rejectWithValue(error);
    }
  }
);

export const replaceSession = createAsyncThunk(
  'session/replace',
  async (
    { logoutType, connectionId }: ReplaceSessionPayload,
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as RootState;
      const sessionId = sessionIdSelector(state);

      if (!sessionId) {
        return;
      }

      await dispatch(
        removeSession({
          logoutType,
        })
      ).unwrap();
      await dispatch(createSession(connectionId)).unwrap();
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'replaceSession',
        })
      );
      return rejectWithValue(error);
    }
  }
);

export const removeSession = createAsyncThunk(
  'session/remove',
  async (
    { logoutType, keepAlive = false }: RemoveSessionPayload,
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as RootState;
      const rnsConnectionId = rnsConnectionIdSelector(state);
      const sessionRemovalStatus = sessionRemovalStatusSelector(state);

      if (!rnsConnectionId || sessionRemovalStatus !== 'idle') {
        addBreadcrumb({
          category: 'session',
          level: 'info',
          message: 'Skipping duplicate removeSession request',
          data: {
            logoutType,
            rnsConnectionId,
          },
        });
        return { skipped: true };
      }

      dispatch(sessionRemovalStarted());
      const removeSessionRequest = {
        rnsConnectionId,
        logoutType,
        ...(keepAlive ? { keepAlive: true } : {}),
      };

      await SessionDaoService.removeSession(removeSessionRequest);
      dispatch(resetSessionId());
      dispatch(sessionRemovalCompleted());
      return { skipped: false };
    } catch (error) {
      dispatch(sessionRemovalFailed());
      return rejectWithValue(error);
    }
  }
);

export const reconnectSession = createAsyncThunk(
  'session/reconnect',
  async (request: ReconnectSessionPayload, { dispatch, rejectWithValue }) => {
    try {
      const sessionService = SessionService.getInstance();
      const session = sessionService.getSession();

      if (!session) {
        return;
      }

      const newSession = await SessionDaoService.reconnectSession({
        ...request,
        oldSession: session,
      });
      sessionService.setSession(newSession);
    } catch (error) {
      dispatch(handleError({ error }));
      return rejectWithValue(error);
    }
  }
);

export const updateSessionByCallPageStatus = createAsyncThunk(
  'session/updateSession',
  async (connectionId: string, { dispatch, getState }) => {
    const state = getState() as RootState;
    const callPageStatus = selectCallPageStatus(state);
    // In these cases user is not in the call from a Relay perspective
    const replaceStatuses: CallPageStatus[] = [
      CallPageStatus.IDLE,
      CallPageStatus.CALL_RECEIVED,
      CallPageStatus.TEAMING_OFFER_CANCELED,
    ];
    // In these cases user in the call from a Relay perspective
    const reconnectStatuses: CallPageStatus[] = [
      CallPageStatus.CONNECTED,
      CallPageStatus.TEAMING_OFFER_ACCEPTED,
      CallPageStatus.COLD_HANDOFF_OFFER_ACCEPTED,
      CallPageStatus.SURVEY,
    ];

    if (replaceStatuses.includes(callPageStatus)) {
      dispatch(
        replaceSession({
          logoutType: LogoutType.ConnectionLoss,
          connectionId,
        })
      );
    }

    if (reconnectStatuses.includes(callPageStatus)) {
      dispatch(
        reconnectSession({
          newSessionId: connectionId,
        })
      );
    }
  }
);

export const finishSession = createAsyncThunk(
  'session/finish',
  async (
    { logoutType, keepAlive, shouldResetMode = true }: SessionFinishPayload,
    { dispatch }
  ) => {
    const sessionService = SessionService.getInstance();
    const result: SessionFinishResult = {
      sessionRemoved: false,
      cleanupSucceeded: true,
    };

    try {
      dispatch(sipRegistrationNotWanted());
      await dispatch(removeSession({ logoutType, keepAlive })).unwrap();
      result.sessionRemoved = true;
    } catch (error) {
      dispatch(
        handleError({
          error,
          disableUserNotification: true,
        })
      );
    }

    if (shouldResetMode) {
      dispatch(resetMultiMode());
    }
    dispatch(
      finishTakingCallsAnalytics('finishSession', LogoutType[logoutType])
    );

    try {
      sessionService.resetSession();
      await RnsConnectionService.cleanup();
    } catch (cleanupError) {
      result.cleanupSucceeded = false;
      dispatch(
        handleError({
          error: cleanupError,
          methodName: 'finishSession cleanup',
        })
      );
    }

    return result;
  }
);
